import { Box, CircularProgress, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Dialog from 'shared/components/widgets/dialog';

import Input from '../../../shared/components/widgets/input';
import { Button as ButtonMui } from '@mui/material'
import Button from 'shared/components/widgets/button';

import markAnyLfsLogo from '@assets/images/markAnyLfsLogo.svg';
import loginBg from '@assets/images/loginBg.svg';

import { adminJwtIssueService } from '@services/public/admin/jwt-issue.service';
import { Controller, useForm } from 'react-hook-form';
import { useAuth } from 'contexts/authContext';
import { VALIDATION_MESSAGE } from 'constants/validation/message';
import PATTERN from 'constants/validation/pattern';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { displayMessage } from '@utils/utilities';
import { toast } from 'react-toastify';

const AdminLogin = () => {
  const [modalErrorLogin, setModalErrorLogin] = useState<{
    isShow: boolean
    errCode: string
  }>({
    isShow: false,
    errCode: ''
  })
  const [params, setSearchParams] = useSearchParams()
  const [isLoading, setLoading] = useState<boolean>(false)
  const { login } = useAuth();
  const { ADM_ID, ADM_PWD } = VALIDATION_MESSAGE.LOGIN.ADMIN;

  const ADMIN_ID_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;
  const adminLoginSchema = yup.object({
    admId: yup
      .string()
      .required(ADM_ID.REQUIRED)
      .min(5, ADM_ID.MIN_LENGTH)
      .max(20, ADM_ID.MAX_LENGTH)
      .matches(ADMIN_ID_PATTERN, ADM_ID.ONLY_UPPER_LOWER_NUM),
    admPwd: yup
      .string()
      .required(ADM_PWD.REQUIRED)
      .min(9, ADM_PWD.MIN_LENGTH)
      .max(20, ADM_PWD.MAX_LENGTH)
      .matches(PATTERN.LOWERCASE_LEAST, ADM_PWD.ATLEAST_LOWER)
      .matches(PATTERN.UPPERCASE_LEAST, ADM_PWD.ATLEAST_UPPER)
      .matches(PATTERN.NUMBER_LEAST, ADM_PWD.ATLEAST_NUMBER)
      .matches(PATTERN.SYMBOL_LEAST, ADM_PWD.ATLEAST_SPECIAL),
  });

  type AdminLoginFormData = yup.InferType<typeof adminLoginSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AdminLoginFormData>({
    defaultValues: {
      admId: '',
      admPwd: ''
    },
    resolver: yupResolver(adminLoginSchema),
  });

  const loginHandle = async (data: AdminLoginFormData) => {
    toast.dismiss()
    setLoading(true)
    const res: any = await adminJwtIssueService(data);
    if (res.statusCode !== 200) {
      toast.error(displayMessage('/admin/jwt/signin', 'POST', res?.responseData?.code), {
        position: 'top-right',
      })
      return setLoading(false)
      // return setModalErrorLogin({
      //   isShow: true,
      //   errCode: res?.responseData?.code
      // })
    }
    login();
    setLoading(false)
  };

  useEffect(() => {
    const error = params.get('error')
    const msg = params.get('msg')
    if (error) {
      params.delete('error')
      setSearchParams(params)
      const msg = displayMessage('/admin/jwt/reissue', 'POST', error)
      toast.error(msg, {
        toastId: 'errMsg',
        autoClose: 10000,
      })
    }
    else if (msg) {
      params.delete('msg')
      setSearchParams(params)
      toast.error(msg, {
        toastId: msg,
        autoClose: 10000,
      })
    }
  }, [params])

  return (
    <>
      <Box
        display="flex"
        width="100%"
        justifyContent="center"
        alignItems="center"
        sx={{
          backgroundImage: `url(${loginBg})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box
          component="form"
          maxWidth={440}
          minWidth={440}
          display="flex"
          flexDirection="column"
          gap="30px"
          bgcolor="var(--base-white)"
          boxShadow="0px 0px 10px 0px rgba(0, 0, 0, 0.25)"
          padding="40px"
          border="1px solid var(--base-white)"
          borderRadius="10px"
          onSubmit={handleSubmit(loginHandle)}
        >
          <Box display="flex" justifyContent="center">
            <img src={markAnyLfsLogo} alt="MarkAny LFS" width={274} height={51} />
          </Box>
          <Typography
            variant="h6"
            color="var(--base-black)"
            textAlign="center"
            fontWeight={700}
          >
            관리자 로그인
          </Typography>
          <Box display="flex" flexDirection="column" gap="26px">
            <Controller
              name="admId"
              control={control}
              render={({ field }) => {
                return (
                  <Input
                    {...field}
                    label="Admin ID"
                    placeholder="관리자 ID를 입력해 주세요."
                    showErrorMsg={!!errors.admId}
                    errorMessage={errors.admId && errors.admId.message}
                    value={field.value}
                  />
                );
              }}
            />

            <Controller
              name="admPwd"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                return (
                  <Input
                    {...field}
                    label="Password"
                    placeholder="비밀번호를 입력해 주세요."
                    isPassword
                    showErrorMsg={!!errors.admPwd}
                    errorMessage={errors.admPwd && errors.admPwd.message}
                    value={field.value}
                  />
                );
              }}
            />
          </Box>
          <Button
            disabled={isLoading}
            variant="contained"
            color="primary"
            type="submit">
            {isLoading
              ?
              <Box display={'flex'} gap={2}>
                기다리세요.
                <CircularProgress size={18} color='secondary' />
              </Box>
              : '로그인'
            }
          </Button>
        </Box>
      </Box>
      <Dialog
        isBackDropClickClose={true}
        closeAction={() => setModalErrorLogin({
          isShow: false,
          errCode: ''
        })}
        open={modalErrorLogin.isShow}
        width={380}
        height={190}
        isCloseIcon={false}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          justifyContent={"center"}
          height={'100%'}
          gap={8}
          paddingTop={3}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            gap={3}
          >
            <Typography fontSize={'20px'}>{displayMessage('/admin/jwt/signin', 'POST', modalErrorLogin.errCode)}</Typography>
          </Box>
          <ButtonMui
            variant="contained"
            color="primary"
            sx={{
              height: '50px',
              width: '80%'
            }}
            type="submit"
            onClick={() => setModalErrorLogin({
              isShow: false,
              errCode: ''
            })}
          >
            다시 시도
          </ButtonMui>
        </Box>
      </Dialog>
    </>
  );
};

export default AdminLogin;
